import { useState, useRef, useEffect } from "react";
import {
  initializeCornerstone,
  enableCornerstoneElement,
  disableCornerstoneElement,
  resetCornerstoneElement,
  purgeFileManager,
  loadDicomFile,
  isValidDicomFile,
  dataUrlToJpgFile,
} from "../../utils/dicomConverter";
import UploadBeforeIcon from "../../assets/images/UploadBefore.svg";
import CloseIcon from "../../assets/images/CloseIcon.svg";
import "./Image.css";
import { DiagnosisApi } from "../../api";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";

// 개별 이미지 업로드 섹션 컴포넌트
const ImageUploadSection = ({
  side,
  imageData,
  onImageDataChange,
  isBothSelected,
}) => {
  const [previewImage, setPreviewImage] = useState(null);
  const [hasDicomImage, setHasDicomImage] = useState(false);
  const [isDicomFile, setIsDicomFile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [originalFile, setOriginalFile] = useState(null);
  const fileInputRef = useRef(null);
  const dicomElementRef = useRef(null);
  const isCornerstoneEnabledRef = useRef(false);
  const elementIdRef = useRef(
    `dicom-element-${side}-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`
  );

  // element에 고유 ID 부여
  useEffect(() => {
    if (dicomElementRef.current && !dicomElementRef.current.id) {
      dicomElementRef.current.id = elementIdRef.current;
    }
  }, []);

  // previewImage 변경 감지
  useEffect(() => {
    // previewImage 변경 감지 로직
  }, [previewImage, side]);

  // imageData가 변경되면 preview 상태 업데이트 (초기 로드 시에만, 변환 중에는 절대 덮어쓰지 않음)
  const isInitialMount = useRef(true);
  const isConvertingRef = useRef(false);

  useEffect(() => {
    // 변환 중이면 절대 상태를 덮어쓰지 않음
    if (isConvertingRef.current) {
      return;
    }

    // previewImage가 이미 설정되어 있고, imageData의 previewImage와 동일하면 스킵
    if (previewImage && imageData?.previewImage === previewImage) {
      return;
    }

    // 초기 마운트 시 또는 imageData가 null로 변경될 때만 상태 업데이트
    if (isInitialMount.current || imageData === null) {
      if (imageData) {
        setPreviewImage(imageData.previewImage);
        setHasDicomImage(!!imageData.previewImage);
        setIsDicomFile(imageData.isDicomFile || false);
        setOriginalFile(imageData.originalFile || null);
      } else {
        setPreviewImage(null);
        setHasDicomImage(false);
        setIsDicomFile(false);
        setOriginalFile(null);
      }
      isInitialMount.current = false;
    }
  }, [imageData, side, previewImage]);

  // DICOM 파일을 JPG로 변환하여 표시
  const convertDicomToJpg = async (file) => {
    // 변환 시작 플래그 설정 (useEffect가 상태를 덮어쓰지 않도록)
    isConvertingRef.current = true;

    setIsLoading(true);
    setHasDicomImage(true);
    setOriginalFile(file);

    let element = dicomElementRef.current;
    let retries = 0;
    while (!element && retries < 20) {
      await new Promise((resolve) => setTimeout(resolve, 50));
      element = dicomElementRef.current;
      retries++;
    }

    if (!element) {
      setIsLoading(false);
      setHasDicomImage(false);
      setOriginalFile(null);
      return;
    }

    try {
      // 이전 이미지 정리 (해당 element만)
      if (isCornerstoneEnabledRef.current) {
        resetCornerstoneElement(element);
      }

      // preview container 표시 (로딩 인디케이터를 보여주기 위해)
      const previewContainer = element.closest(".image-preview-container");
      if (previewContainer) {
        previewContainer.style.display = "flex";
      }

      // Cornerstone viewer는 백그라운드에서만 사용 (화면에 표시하지 않음)
      // 하지만 렌더링을 위해 화면에 보이도록 설정 (opacity 0으로 숨김)
      // display: none이면 Cornerstone이 렌더링할 수 없으므로 block으로 설정
      element.style.display = "block";
      element.style.visibility = "visible";
      element.style.width = "960px";
      element.style.height = "720px";
      // element.style.minHeight = "720px";
      element.style.position = "absolute";
      element.style.left = "0";
      element.style.top = "0";
      element.style.opacity = "0";
      element.style.pointerEvents = "none";
      element.style.zIndex = "-1";
      element.style.overflow = "hidden";

      await new Promise((resolve) => setTimeout(resolve, 200));

      if (!isCornerstoneEnabledRef.current) {
        const enabled = enableCornerstoneElement(element);
        if (!enabled) {
          throw new Error("Cornerstone 엘리먼트 활성화에 실패했습니다.");
        }
        isCornerstoneEnabledRef.current = true;
      }

      await new Promise((resolve) => setTimeout(resolve, 100));

      const { jpgDataUrl } = await loadDicomFile(file, element);

      // 변환된 이미지 설정
      if (!jpgDataUrl) {
        console.error(`[${side}] ❌ jpgDataUrl이 null입니다!`);
        throw new Error("DICOM 변환 실패: 이미지 데이터를 생성할 수 없습니다.");
      }

      // DataURL 유효성 검사
      if (!jpgDataUrl.startsWith("data:image")) {
        console.error(
          `[${side}] ❌ 잘못된 이미지 형식:`,
          jpgDataUrl.substring(0, 50)
        );
        throw new Error("DICOM 변환 실패: 잘못된 이미지 형식입니다.");
      }

      // 변환 완료 후 즉시 Cornerstone viewer 숨기기 및 비활성화
      if (element && isCornerstoneEnabledRef.current) {
        try {
          // Cornerstone element 완전히 비활성화 (계속 렌더링 방지)
          disableCornerstoneElement(element);
          resetCornerstoneElement(element);
          element.style.display = "none";
          element.style.visibility = "hidden";
          isCornerstoneEnabledRef.current = false;
        } catch (e) {
          console.error("Cornerstone element 정리 중 오류:", e);
        }
      }

      // 상태 업데이트를 나중에 수행 (Cornerstone 비활성화 후)
      // 변환된 이미지를 즉시 저장 (이후 useEffect가 덮어쓰지 못하도록)
      const savedImageDataUrl = jpgDataUrl; // 변수에 저장하여 안전하게 보관

      // 상태 업데이트를 먼저 수행
      setPreviewImage(savedImageDataUrl);
      setIsDicomFile(true);
      setIsLoading(false);

      // 부모 컴포넌트에 데이터 전달 (상태 업데이트 후, 하지만 isConvertingRef는 아직 true)
      // 이렇게 하면 onImageDataChange로 인한 imageData 업데이트가 useEffect에서 스킵됨
      if (onImageDataChange) {
        const jpgFile = dataUrlToJpgFile(
          savedImageDataUrl,
          "converted_image.jpg"
        );
        onImageDataChange({
          originalFile: file,
          jpgFile: jpgFile,
          previewImage: savedImageDataUrl,
          isDicomFile: true,
        });
      }

      // onImageDataChange로 인한 imageData 업데이트가 useEffect에서 처리된 후에 플래그 해제
      // 다음 렌더링 사이클에서 해제하도록 setTimeout 사용
      setTimeout(() => {
        isConvertingRef.current = false;
      }, 0);
    } catch (err) {
      console.error(`[${side}] ❌ DICOM 로딩/변환 오류:`, err);
      isConvertingRef.current = false; // 에러 발생 시에도 플래그 해제
      setIsLoading(false);
      setHasDicomImage(false);
      setOriginalFile(null);

      const previewContainer = element.closest(".image-preview-container");
      if (previewContainer) {
        previewContainer.style.display = "none";
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (isValidDicomFile(file)) {
      setIsDicomFile(true);
      setTimeout(() => {
        convertDicomToJpg(file);
      }, 50);
    } else {
      setIsDicomFile(false);
      setIsLoading(true);
      setOriginalFile(file);

      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target.result;
        setPreviewImage(dataUrl);
        setHasDicomImage(true);
        setIsLoading(false);

        if (onImageDataChange) {
          const jpgFile = dataUrlToJpgFile(
            dataUrl,
            file.name.replace(/\.[^/.]+$/, ".jpg")
          );
          onImageDataChange({
            originalFile: file,
            jpgFile: jpgFile,
            previewImage: dataUrl,
            isDicomFile: false,
          });
        }
      };
      reader.onerror = (error) => {
        console.error(`[${side}] 파일 읽기 오류:`, error);
        setIsLoading(false);
        setOriginalFile(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSelectFile = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      const fileName = file.name.toLowerCase();
      const isValidImage =
        fileName.endsWith(".jpg") ||
        fileName.endsWith(".jpeg") ||
        fileName.endsWith(".png") ||
        isValidDicomFile(file);

      if (!isValidImage) {
        return;
      }

      if (isValidDicomFile(file)) {
        setIsDicomFile(true);
        setTimeout(() => {
          convertDicomToJpg(file);
        }, 50);
      } else {
        setIsDicomFile(false);
        setIsLoading(true);
        setOriginalFile(file);

        const reader = new FileReader();
        reader.onload = (e) => {
          const dataUrl = e.target.result;
          setPreviewImage(dataUrl);
          setHasDicomImage(true);
          setIsLoading(false);

          if (onImageDataChange) {
            const jpgFile = dataUrlToJpgFile(
              dataUrl,
              file.name.replace(/\.[^/.]+$/, ".jpg")
            );
            onImageDataChange({
              originalFile: file,
              jpgFile: jpgFile,
              previewImage: dataUrl,
              isDicomFile: false,
            });
          }
        };
        reader.onerror = (error) => {
          console.error(`[${side}] 파일 읽기 오류:`, error);
          setIsLoading(false);
          setOriginalFile(null);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleRemoveImage = () => {
    setPreviewImage(null);
    setHasDicomImage(false);
    setIsDicomFile(false);
    setOriginalFile(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    if (dicomElementRef.current && isCornerstoneEnabledRef.current) {
      resetCornerstoneElement(dicomElementRef.current);
    }

    // 전체 fileManager를 정리하지 않고 해당 element만 reset
    // purgeFileManager()는 호출하지 않음 (다른 섹션에 영향 주지 않기 위해)

    if (onImageDataChange) {
      onImageDataChange(null);
    }
  };

  const isComplete = !!imageData;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        width: "100%",
        height: "100%",
      }}
    >
      <div
        className={`image-header ${isComplete ? "image-header-complete" : ""}`}
      >
        {side}
      </div>
      <div className="image-container">
        <div className="image-content">
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.dcm,.dicom,application/dicom"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />

          {!hasDicomImage && !isLoading && (
            <div
              className="image-upload-area"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleSelectFile}
            >
              <UploadBeforeIcon />
              <div className="image-upload-title">
                <div>이미지를 드래그 하거나 클릭하여 업로드</div>
              </div>
              <div className="image-upload-hint">
                <div>지원 형식: JPG, JPEG, PNG, DICOM</div>
              </div>
            </div>
          )}
          <div
            className="image-preview-container"
            style={{
              display: hasDicomImage ? "flex" : "none",
            }}
          >
            <div className="image-preview-wrapper">
              {isDicomFile && isLoading && (
                <>
                  <div
                    ref={dicomElementRef}
                    className="dicom-viewer"
                    style={{
                      display: "none",
                      visibility: "hidden",
                      position: "absolute",
                      left: "-9999px",
                    }}
                  ></div>
                  <div className="image-loading">DICOM 이미지 변환 중...</div>
                </>
              )}
              {previewImage && !isLoading && (
                <img
                  key={`${previewImage.substring(0, 50)}-${Date.now()}`} // 고유 키로 안전하게 렌더링
                  src={previewImage}
                  alt="업로드된 이미지"
                  className="image-preview"
                  style={{
                    display: "block",
                    maxWidth: "100%",
                    height: "auto",
                  }}
                  onLoad={(e) => {
                    if (!(e.target.complete && e.target.naturalWidth > 0)) {
                      console.error(`[${side}] ⚠️ 이미지 로드 상태 이상:`, {
                        complete: e.target.complete,
                        naturalWidth: e.target.naturalWidth,
                        naturalHeight: e.target.naturalHeight,
                      });
                    }

                    if (dicomElementRef.current && isDicomFile) {
                      try {
                        if (isCornerstoneEnabledRef.current) {
                          disableCornerstoneElement(dicomElementRef.current);
                          resetCornerstoneElement(dicomElementRef.current);
                          isCornerstoneEnabledRef.current = false;
                        }
                        // element 완전히 제거
                        dicomElementRef.current.style.display = "none";
                        dicomElementRef.current.style.visibility = "hidden";
                        dicomElementRef.current.style.width = "0";
                        dicomElementRef.current.style.height = "0";
                      } catch (err) {
                        console.error(
                          `[${side}] ⚠️ Cornerstone 정리 중 오류:`,
                          err
                        );
                      }
                    }
                  }}
                  onError={(e) => {
                    console.error(`[${side}] ❌ 이미지 로드 실패:`, {
                      srcLength: previewImage?.length,
                      srcStart: previewImage?.substring(0, 50),
                      error: e,
                    });
                    e.target.style.display = "none";
                  }}
                />
              )}
              {isLoading && !isDicomFile && (
                <div className="image-loading">이미지 로딩 중...</div>
              )}
            </div>
          </div>
          {hasDicomImage && !isLoading && (
            <div
              className="image-delete-button"
              onClick={handleRemoveImage}
              style={{
                cursor: "pointer",
              }}
            >
              <CloseIcon />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// 로딩 메시지 배열
const loadingMessages = [
  "초음파 이미지 스캔 및 전 처리 중...",
  "회전근개(극상근) 영역 추출 중...",
  "AI 알고리즘 위험도 분석 중...",
];

const Image = ({
  setProgressWidth,
  doctorId,
  leftShoulder,
  rightShoulder,
  leftImageData,
  rightImageData,
  setLeftImageData,
  setRightImageData,
  leftSurvey,
  rightSurvey,
  onBack,
  apiLoading,
  setApiLoading,
  onNext,
  result,
  setResult,
}) => {
  useEffect(() => {
    initializeCornerstone();
    return () => {
      purgeFileManager();
    };
  }, []);

  const isBothSelected = leftShoulder && rightShoulder;

  // 이미지 업로드 완료 여부 확인
  const isLeftComplete = () => {
    return !!leftImageData;
  };

  const isRightComplete = () => {
    return !!rightImageData;
  };

  // progressWidth 업데이트
  useEffect(() => {
    const leftComplete = leftShoulder ? isLeftComplete() : true;
    const rightComplete = rightShoulder ? isRightComplete() : true;

    if (leftComplete && rightComplete) {
      setProgressWidth(66);
    } else {
      setProgressWidth(66);
    }
  }, [
    leftShoulder,
    rightShoulder,
    leftImageData,
    rightImageData,
    setProgressWidth,
  ]);

  const handleNext = async () => {
    // 모든 선택된 어깨의 이미지가 업로드되었는지 확인
    const leftComplete = leftShoulder ? isLeftComplete() : true;
    const rightComplete = rightShoulder ? isRightComplete() : true;

    if (leftComplete && rightComplete) {
      setApiLoading(true);
      const startTime = Date.now(); // 시작 시간 기록
      const isBothSelected = leftShoulder && rightShoulder;
      const minLoadingTime = isBothSelected ? 15000 : 9000;

      // 최소 시간이 지났는지 확인하고 남은 시간만큼 대기하는 함수
      const ensureMinLoadingTime = async () => {
        const elapsedTime = Date.now() - startTime;
        const remainingTime = minLoadingTime - elapsedTime;

        if (remainingTime > 0) {
          await new Promise((resolve) => setTimeout(resolve, remainingTime));
        }
        setApiLoading(false);
      };

      try {
        if (leftShoulder && rightShoulder) {
          const LeftSurvey =
            leftSurvey.question1 +
            "," +
            (Array.isArray(leftSurvey.question2)
              ? leftSurvey.question2.join(" ")
              : leftSurvey.question2) +
            "," +
            (Array.isArray(leftSurvey.question3)
              ? leftSurvey.question3.join(" ")
              : leftSurvey.question3);
          const leftResponse = await DiagnosisApi.diagnosis({
            doctorId: doctorId,
            survey: LeftSurvey,
            position: "LEFT",
            img: leftImageData.jpgFile,
            dicomImg: leftImageData.originalFile,
          });
          const RightSurvey =
            rightSurvey.question1 +
            "," +
            (Array.isArray(rightSurvey.question2)
              ? rightSurvey.question2.join(" ")
              : rightSurvey.question2) +
            "," +
            (Array.isArray(rightSurvey.question3)
              ? rightSurvey.question3.join(" ")
              : rightSurvey.question3);
          const rightResponse = await DiagnosisApi.diagnosis({
            doctorId: doctorId,
            surgeryId: leftResponse.data.surgeryId,
            survey: RightSurvey,
            position: "RIGHT",
            img: rightImageData.jpgFile,
            dicomImg: rightImageData.originalFile,
          });
          await ensureMinLoadingTime();
          const transformedResult = {
            left: {
              diagnosisList: rightResponse.data.diagnosisList[0]
                ? [rightResponse.data.diagnosisList[0]]
                : [],
              insertDate:
                rightResponse.data.insertDate || new Date().toISOString(),
              surgeryId:
                rightResponse.data.surgeryId || leftResponse.data.surgeryId,
            },
            right: {
              diagnosisList: rightResponse.data.diagnosisList[1]
                ? [rightResponse.data.diagnosisList[1]]
                : [],
              insertDate:
                rightResponse.data.insertDate || new Date().toISOString(),
              surgeryId:
                rightResponse.data.surgeryId || leftResponse.data.surgeryId,
            },
          };

          setResult((prev) => ({
            ...prev,
            ...transformedResult,
          }));
          onNext();
          return;
        }
        if (leftShoulder && !rightShoulder) {
          const LeftSurvey =
            leftSurvey.question1 +
            "," +
            (Array.isArray(leftSurvey.question2)
              ? leftSurvey.question2.join(" ")
              : leftSurvey.question2) +
            "," +
            (Array.isArray(leftSurvey.question3)
              ? leftSurvey.question3.join(" ")
              : leftSurvey.question3);
          const leftResponse = await DiagnosisApi.diagnosis({
            doctorId: doctorId,
            survey: LeftSurvey,
            position: "LEFT",
            img: leftImageData.jpgFile,
            dicomImg: leftImageData.originalFile,
          });
          await ensureMinLoadingTime();
          const leftDiagnosisData =
            leftResponse.data.diagnosisList?.[0] || leftResponse.data;
          const transformedResult = {
            left: {
              diagnosisList: leftResponse.data.diagnosisList
                ? leftResponse.data.diagnosisList.filter(
                    (item) => item.position === "LEFT"
                  )
                : [leftDiagnosisData],
              insertDate:
                leftResponse.data.insertDate || new Date().toISOString(),
              surgeryId: leftResponse.data.surgeryId,
            },
          };

          setResult((prev) => ({
            ...prev,
            ...transformedResult,
          }));
          onNext();
          return;
        }
        if (!leftShoulder && rightShoulder) {
          const RightSurvey =
            rightSurvey.question1 +
            "," +
            (Array.isArray(rightSurvey.question2)
              ? rightSurvey.question2.join(" ")
              : rightSurvey.question2) +
            "," +
            (Array.isArray(rightSurvey.question3)
              ? rightSurvey.question3.join(" ")
              : rightSurvey.question3);
          const rightResponse = await DiagnosisApi.diagnosis({
            doctorId: doctorId,
            survey: RightSurvey,
            position: "RIGHT",
            img: rightImageData.jpgFile,
            dicomImg: rightImageData.originalFile,
          });
          await ensureMinLoadingTime();

          const rightDiagnosisData =
            rightResponse.data.diagnosisList?.[0] || rightResponse.data;
          const transformedResult = {
            right: {
              diagnosisList: rightResponse.data.diagnosisList
                ? rightResponse.data.diagnosisList.filter(
                    (item) => item.position === "RIGHT"
                  )
                : [rightDiagnosisData],
              insertDate:
                rightResponse.data.insertDate || new Date().toISOString(),
              surgeryId: rightResponse.data.surgeryId,
            },
          };

          setResult((prev) => ({
            ...prev,
            ...transformedResult,
          }));
          onNext();
          return;
        }
      } catch (error) {
        console.error("AI 분석 오류:", error);
        await ensureMinLoadingTime();
      }
    }
  };
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  useEffect(() => {
    if (apiLoading) {
      setCurrentMessageIndex(0);

      const timers = [];

      if (isBothSelected) {
        // 둘 다 선택
        const firstDelay = 4000 + Math.random() * 1000; // 4~5
        const thirdDelay = 5000 + Math.random() * 1000; // 최소 5~

        const timer1 = setTimeout(() => {
          setCurrentMessageIndex(1);

          const timer3 = setTimeout(() => {
            setCurrentMessageIndex(2);
          }, thirdDelay);
          timers.push(timer3);
        }, firstDelay);
        timers.push(timer1);
      } else {
        // 하나 선택
        const firstDelay = 2000 + Math.random() * 1000; // 2~3
        const thirdDelay = 3000 + Math.random() * 1000; // 최소 3~

        const timer1 = setTimeout(() => {
          setCurrentMessageIndex(1);

          const timer3 = setTimeout(() => {
            setCurrentMessageIndex(2);
          }, thirdDelay);
          timers.push(timer3);
        }, firstDelay);
        timers.push(timer1);
      }

      return () => {
        timers.forEach((timer) => clearTimeout(timer));
      };
    } else {
      setCurrentMessageIndex(0);
    }
  }, [apiLoading, isBothSelected]);

  // Lottie 스크립트 로드
  useEffect(() => {
    if (apiLoading) {
      const script = document.createElement("script");
      script.src =
        "https://unpkg.com/@lottiefiles/dotlottie-wc@0.8.5/dist/dotlottie-wc.js";
      script.type = "module";
      script.async = true;
      document.head.appendChild(script);

      return () => {
        if (document.head.contains(script)) {
          document.head.removeChild(script);
        }
      };
    }
  }, [apiLoading]);
  return (
    <div className="image-wrapper">
      {apiLoading ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <div className="image-loading-message-container">
            <div className="image-loading-message-title">
              AI 정밀 분석 진행 중
            </div>
            {loadingMessages.map((message, index) => (
              <div
                key={index}
                className="image-loading-message-text"
                style={{
                  display: index === currentMessageIndex ? "block" : "none",
                }}
              >
                {message}
              </div>
            ))}
          </div>
          <dotlottie-wc
            src="https://lottie.host/7f6b9015-4646-4498-83e5-7579a3304ed4/VN9JBgkjAV.lottie"
            style={{ height: "300px", aspectRatio: "1/1", margin: "20px 0px" }}
            autoplay
            loop
          ></dotlottie-wc>
          {/* <div className="image-loading-bar">loading Bar</div> */}
          <Box sx={{ width: "90%" }}>
            <LinearProgress
              sx={{
                height: 7,
                borderRadius: 999,
              }}
            />
          </Box>

          <div>
            <div className="image-loading-bar-text">
              잠시만 기다려 주세요.
              <br />
              진단 보조 리포트를 생성하고 있습니다.
            </div>
          </div>
        </div>
      ) : (
        <>
          <div
            className={`image-containers ${
              isBothSelected ? "image-containers-row" : ""
            }`}
          >
            {leftShoulder && (
              <ImageUploadSection
                side="Left"
                imageData={leftImageData}
                onImageDataChange={setLeftImageData}
                isBothSelected={isBothSelected}
              />
            )}
            {rightShoulder && (
              <ImageUploadSection
                side="Right"
                imageData={rightImageData}
                onImageDataChange={setRightImageData}
                isBothSelected={isBothSelected}
              />
            )}
          </div>
          <div className="image-button-container">
            <button className="image-button-back" onClick={onBack}>
              이전
            </button>
            <button
              className="image-button"
              onClick={handleNext}
              disabled={
                (leftShoulder && !isLeftComplete()) ||
                (rightShoulder && !isRightComplete())
              }
            >
              AI 분석 시작
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Image;
